import type { ClientMsg, PlayerInfo, PlayerState, ServerMsg } from '../../server/protocol.ts';

export type NetEvents = {
  welcome: (me: string, players: (PlayerInfo & { st?: PlayerState })[], rocks: [string, number][]) => void;
  join: (p: PlayerInfo) => void;
  info: (p: PlayerInfo) => void;
  leave: (id: string) => void;
  states: (time: number, list: [string, PlayerState][]) => void;
  fire: (id: string, frame: string, p: [number, number, number], d: [number, number, number]) => void;
  rock: (id: string, time: number, by: string) => void;
  chat: (id: string, name: string, text: string) => void;
  status: (s: 'connecting' | 'online' | 'offline') => void;
};

/** WebSocket client with reconnect and clock sync (server time = Date.now() + offset). */
export class NetClient {
  private ws: WebSocket | null = null;
  private hello: { name: string; ship: string; color: string } | null = null;
  private handlers: Partial<NetEvents> = {};
  private pingTimer = 0;
  private retry = 0;
  private samples: { rtt: number; offset: number }[] = [];
  offset = 0;
  rtt = 0;
  id: string | null = null;
  online = false;

  constructor(private readonly url: string) {}

  on<K extends keyof NetEvents>(k: K, fn: NetEvents[K]): void {
    this.handlers[k] = fn;
  }

  connect(): void {
    this.handlers.status?.('connecting');
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      this.online = true;
      this.handlers.status?.('online');
      this.ping();
      clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => this.ping(), 3000);
      if (this.hello) this.send({ t: 'hello', ...this.hello });
    };
    ws.onmessage = (e) => {
      let msg: ServerMsg;
      try {
        msg = JSON.parse(e.data);
      } catch {
        return;
      }
      this.dispatch(msg);
    };
    ws.onclose = () => {
      this.online = false;
      this.id = null;
      clearInterval(this.pingTimer);
      this.handlers.status?.('offline');
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    const delay = Math.min(15000, 1000 * 2 ** this.retry++);
    setTimeout(() => this.connect(), delay);
  }

  join(name: string, ship: string, color: string): void {
    this.hello = { name, ship, color };
    if (this.online) this.send({ t: 'hello', ...this.hello });
  }

  send(msg: ClientMsg): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  private ping(): void {
    this.send({ t: 'ping', c: Date.now() });
  }

  private dispatch(msg: ServerMsg): void {
    const h = this.handlers;
    switch (msg.t) {
      case 'pong': {
        const now = Date.now();
        const rtt = now - msg.c;
        this.samples.push({ rtt, offset: msg.s + rtt / 2 - now });
        if (this.samples.length > 8) this.samples.shift();
        // Use the lowest-RTT samples: they have the least asymmetric delay.
        const best = [...this.samples].sort((a, b) => a.rtt - b.rtt).slice(0, 3);
        this.offset = best.reduce((a, s) => a + s.offset, 0) / best.length;
        this.rtt = best[0].rtt;
        break;
      }
      case 'welcome':
        this.id = msg.id;
        this.offset = msg.time - Date.now();
        h.welcome?.(msg.id, msg.players, msg.rocks);
        break;
      case 'join':
        h.join?.(msg.player);
        break;
      case 'info':
        h.info?.(msg.player);
        break;
      case 'leave':
        h.leave?.(msg.id);
        break;
      case 'states':
        h.states?.(msg.time, msg.list);
        break;
      case 'fire':
        h.fire?.(msg.id, msg.f, msg.p, msg.d);
        break;
      case 'rock':
        h.rock?.(msg.id, msg.time, msg.by);
        break;
      case 'chat':
        h.chat?.(msg.id, msg.name, msg.text);
        break;
    }
  }
}
